import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LOCALE } from "../config/app";
import { detectBrowserTimezone } from "../lib/timezone";
import { readStoredJson, writeStoredJson } from "../lib/storage";
import type { AppPreferences } from "../types/models";
import { useI18n } from "../i18n/I18nProvider";

const PREFERENCES_STORAGE_KEY = "water-tracker-preferences";

const defaultPreferences: AppPreferences = {
  locale: DEFAULT_LOCALE,
  theme: "system",
  volumeUnit: "ml",
  lengthUnit: "cm",
  weightUnit: "kg",
  timezoneMode: "detected",
  fixedTimezone: detectBrowserTimezone(),
  showStreaks: false
};

type PreferencesContextValue = {
  preferences: AppPreferences;
  updatePreferences: (patch: Partial<AppPreferences>) => void;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { setLocale } = useI18n();
  const [preferences, setPreferences] = useState<AppPreferences>(() => ({
    ...defaultPreferences,
    ...readStoredJson<Partial<AppPreferences>>(PREFERENCES_STORAGE_KEY, {})
  }));

  useEffect(() => {
    document.documentElement.dataset.theme = resolveTheme(preferences.theme);
    document.documentElement.dataset.themePreference = preferences.theme;
    document.querySelector("meta[name='theme-color']")?.setAttribute("content", preferences.theme === "dark" ? "#111716" : "#f7f8f8");
  }, [preferences.theme]);

  useEffect(() => {
    setLocale(preferences.locale);
  }, [preferences.locale, setLocale]);

  const updatePreferences = (patch: Partial<AppPreferences>) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      writeStoredJson(PREFERENCES_STORAGE_KEY, next);
      return next;
    });
  };

  const value = useMemo(() => ({ preferences, updatePreferences }), [preferences]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (!value) {
    throw new Error("usePreferences must be used inside PreferencesProvider");
  }
  return value;
}

function resolveTheme(theme: AppPreferences["theme"]): "light" | "dark" {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}
